/**
 * Shared API error handling utilities.
 *
 * Provides type-safe error extraction from RTK Query responses.
 * Consolidates duplicate error handling patterns across the codebase.
 */
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/** Union type for all possible RTK Query error shapes */
export type ApiError = FetchBaseQueryError | SerializedError;

/**
 * Type guard to check if an error is an RTK Query error.
 */
export const isApiError = (error: unknown): error is ApiError => {
  if (typeof error !== 'object' || error === null) return false;
  return 'status' in error || 'message' in error;
};

/**
 * Type guard for FetchBaseQueryError with data.
 */
const isFetchBaseQueryErrorWithData = (
  error: unknown,
): error is FetchBaseQueryError & {
  data: Record<string, unknown>;
} => {
  if (typeof error !== 'object' || error === null) return false;
  if (!('status' in error)) return false;
  const err = error as FetchBaseQueryError;
  return typeof err.data === 'object' && err.data !== null;
};

/**
 * Extract field validation errors from Django REST Framework response.
 * Handles dict format like: { "field_name": ["Error message"] }
 */
const extractValidationError = (data: Record<string, unknown>): string | null => {
  // Skip known message fields
  if (data.message || data.detail || data.error) return null;

  // Look for validation error dict pattern using array methods (no for...of per lint rules)
  const validationKey = Object.keys(data).find((key) => {
    const value = data[key];
    return Array.isArray(value) && value.length > 0 && typeof value[0] === 'string';
  });

  if (validationKey) {
    const value = data[validationKey] as string[];
    return `${validationKey}: ${value[0]}`;
  }

  return null;
};

/**
 * Extract a user-friendly error message from an RTK Query error.
 *
 * Handles:
 * - FetchBaseQueryError with data.message or data.detail
 * - FetchBaseQueryError status codes (404, 403, 500, etc.)
 * - SerializedError with message
 * - Unknown errors with fallback
 *
 * @example
 * ```ts
 * try {
 *   await updateItem(data).unwrap();
 * } catch (error) {
 *   toast.error(getApiErrorMessage(error, 'Failed to update item'));
 * }
 * ```
 */
export const getApiErrorMessage = (
  error: unknown,
  fallback = 'An unexpected error occurred',
): string => {
  // Handle FetchBaseQueryError with data payload
  if (isFetchBaseQueryErrorWithData(error)) {
    const { data } = error;
    // Check common error message fields
    if (typeof data.error === 'string') return data.error;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.detail === 'string') return data.detail;
    // Check for validation error dict (Django REST Framework pattern)
    const validationError = extractValidationError(data);
    if (validationError) return validationError;
  }

  // Handle FetchBaseQueryError by status
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const fetchError = error as FetchBaseQueryError;

    if (fetchError.status === 'FETCH_ERROR') {
      return 'Network error. Please check your connection.';
    }
    if (fetchError.status === 'PARSING_ERROR') {
      return 'Invalid response from server.';
    }
    if (fetchError.status === 'TIMEOUT_ERROR') {
      return 'Request timed out. Please try again.';
    }
    if (typeof fetchError.status === 'number') {
      if (fetchError.status === 404) return 'Resource not found.';
      if (fetchError.status === 403) return 'Access denied.';
      if (fetchError.status === 401) return 'Please sign in to continue.';
      if (fetchError.status >= 500)
        return 'Server error. Please try again later.';
    }
  }

  // Handle SerializedError
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as SerializedError).message === 'string'
  ) {
    return (error as SerializedError).message as string;
  }

  return fallback;
};
